/**
 * Default English strings for @puffin-core/login-shell.
 * Consumers can override via a `t()` translation prop with the same keys.
 */

// 🔴 The React-FREE `/strings-kit` subpath, never the package barrel — this file is part of the
// leaf-only `strings/` subtree `@puffin-core/i18n-namespaces` imports at app boot, and login-shell's
// barrel reaches the whole login composite.
import { createInterpolator } from '@puffin-core/i18n-bootstrap/strings-kit';

export const DEFAULT_LOGIN_STRINGS = Object.freeze({
  /* Page chrome */
  'login.title': 'Welcome Back',
  'login.subtitle': 'Sign in to continue',
  'login.pageTitle': 'Sign In',
  // BCU-001 (2026-07-25): the arrow is the COMPONENT's job, not the string's —
  // a `←` baked into a translation cannot mirror in a right-to-left locale.
  'login.backToHome': 'Back to Home',

  /* Accessible names (rendered only to assistive tech — and only in English
     until AAS-I18N-002 W8 / ruling 25, because `aria-label="Login form"` was a
     hardcoded literal in a SHARED PACKAGE: RECIPE miss-class 9. No app-scoped
     gate can see a string that lives in `packages/**`, and this one renders on
     the first screen of all ten apps. */
  'login.form.ariaLabel': 'Login form',

  /* Field labels */
  'login.email.label': 'Email',
  'login.password.label': 'Password',
  'login.remember.plain': 'Remember me',

  /* Buttons */
  'login.submit': 'Sign In',
  'login.submit.loading': 'Signing in...',
  'login.unlock': 'Unlock',
  'login.unlock.loading': 'Unlocking...',
  'login.passkey.submit': 'Sign in with Passkey',
  'login.passkey.submitSecurityKey': 'Sign in with Security Key',
  'login.passkey.tryAgain': 'Try Again',
  'login.passkey.unlock': 'Unlock with Passkey',
  'login.passkey.unlockSecurityKey': 'Unlock with Security Key',

  /* Links */
  'login.forgot.prompt': 'Forgot password?',
  'login.forgot.action': 'Send reset email',
  'login.contact.prompt': 'Need access?',
  'login.contact.action': 'Contact your store manager',
  'login.notYourCompany.prompt': 'Not your company?',
  'login.notYourCompany.action': 'Sign in elsewhere',

  /* N152 — the marketing apex's held sign-in: the person enrols at their company's own sign-in page. */
  'login.mfaEnrollmentHandoff.title': 'Set up two-step verification',
  'login.mfaEnrollmentHandoff.body':
    "Your account needs two-step verification set up again before you can sign in. Continue at your company's sign-in page to set it up.",
  'login.mfaEnrollmentHandoff.continue': 'Continue to your sign-in page',
  'login.mfaEnrollmentHandoff.back': 'Back',
  // SECREQ S6d (owner ruling Q5): a passkey hold in an app that sets none up hands the person to one that does.
  'login.passkeySetupHandoff.title': 'Set up a passkey',
  'login.passkeySetupHandoff.body':
    "Your company requires a passkey for your role, and {appName} can't set one up. Set one up in one of these apps, then sign in here again.",
  'login.passkeySetupHandoff.openApp': 'Set up in {appName}',
  'login.passkeySetupHandoff.back': 'Back',

  /* Auth method selector */
  'method.emailPassword': 'Email & Password',
  'method.passkey': 'Passkey',
  'method.pin': 'PIN',
  'method.selectorLabel': 'Select authentication method',
  'method.checking': 'Checking...',
  'method.unavailableTitle': 'Not available right now',
  'method.unavailableHint': 'Not available right now. The reason is listed below.',
  'method.unavailableLine': '{method}: {reason}',

  'login.passkey.fallback': 'Use email and password instead',

  /* PalmPuffin Authenticator push 2FA (AUTHENTICATOR-CHALLENGE-FANOUT, 2026-08-29).
     🔴 The number is NOT a secret and the copy must not imply it is: number matching exists so a
     user who did not initiate a sign-in sees a number they cannot possibly have, and refuses.
     "Enter this number in PalmPuffin", never "your code is". */
  'authenticator.title': 'Approve sign-in',
  'authenticator.subtitle': 'Enter this number in PalmPuffin to approve:',
  'authenticator.numberAria': 'Approval number {number}',
  'authenticator.waiting': 'Waiting for approval...',
  'authenticator.useCodeInstead': 'Use a code instead',
  'authenticator.cancel': 'Cancel',
  'authenticator.status.approved': 'Approved',
  'authenticator.status.denied': 'This sign-in was denied on your device.',
  'authenticator.status.expired': 'This request expired. Please sign in again.',
  /* Poll failures surfaced by `useAuthenticatorConfig`. `useAuthenticatorChallenge` renders a poll
     error as a transient warning and KEEPS polling, so both read as "still trying", not "over". */
  'authenticator.error.network': 'Network error. Please check your connection and try again.',
  'authenticator.error.incomplete': 'Sign-in could not be completed. Please sign in again.',
  'authenticator.error.code': 'That code is not right. Check your authenticator app and try again.',
  // N138: the code form words each failure by its CODE — these two complete its set.
  'authenticator.error.rateLimited': 'Too many attempts. Please wait a moment and try again.',
  'authenticator.error.codeCheckFailed': 'Your code could not be checked. Please try again.',
  'authenticator.codePrompt': 'Enter the 6-digit code from your authenticator app:',
  'authenticator.submitCode': 'Verify code',
  'authenticator.backToPush': 'Back to approval request',

  /* Passkey states */
  'passkey.unsupported':
    'Passkey authentication is not supported on this device/browser. Please use email and password to sign in.',
  'passkey.noPlatform':
    'No platform authenticator (such as Touch ID or Windows Hello) was detected. You may still be able to use a security key.',
  'passkey.loading': 'Waiting for passkey authentication...',
  /* Passkey sign-in failures (USER-FACING-GEMS L1) — chosen by CLASS in `usePasskeyLogin`; a
     thrown message is never rendered. */
  'passkey.error.cancelled': 'Passkey sign-in was cancelled. Try again, or use email and password.',
  'passkey.error.notRecognized':
    "That passkey isn't registered for this account. Try another passkey, or use email and password.",
  'passkey.error.rateLimited': 'Too many attempts. Please wait a moment and try again.',
  'passkey.error.unavailable':
    'Passkey sign-in is unavailable for a moment. Please try again, or use email and password.',
  'passkey.error.failed':
    "Passkey sign-in didn't complete. Check your connection and try again, or use email and password.",

  /* Password reset modal */
  'modal.passwordReset.title': 'Reset Password',
  'modal.passwordReset.submit.loading': 'Sending...',
  'modal.passwordReset.passwordChangedBody':
    'Your password has been updated. You can now sign in with your new password.',
  'modal.passwordReset.close': 'Close',

  /* Password reset multi-step (OTP) flow */
  'modal.passwordReset.multiStep.emailBody':
    'Enter your email address and we will send you a 6-digit verification code.',
  'modal.passwordReset.multiStep.emailSubmit': 'Send Verification Code',
  'modal.passwordReset.multiStep.codeBody':
    'We sent a 6-digit code to {email}. Enter it below to continue.',
  'modal.passwordReset.multiStep.codeLabel': 'Verification Code',
  'modal.passwordReset.multiStep.codeSubmit': 'Verify Code',
  'modal.passwordReset.multiStep.resendCode': 'Resend Code',
  'modal.passwordReset.multiStep.newPasswordBody':
    'Choose a new password. It must meet every requirement below.',
  'modal.passwordReset.multiStep.newPasswordLabel': 'New Password',
  'modal.passwordReset.multiStep.confirmPasswordLabel': 'Confirm New Password',
  'modal.passwordReset.multiStep.newPasswordSubmit': 'Change Password',
  'modal.passwordReset.stepOf': 'Step {current} of {total}',
  'modal.passwordReset.form.email': 'Password reset form',
  'modal.passwordReset.form.code': 'Verification code form',
  'modal.passwordReset.form.newPassword': 'New password form',
  'modal.passwordReset.multiStep.differentEmail': 'Use a different email',
  'modal.passwordReset.multiStep.resendIn': 'Resend code in {seconds}s',
  'modal.passwordReset.error.network':
    "We couldn't reach the server. Check your connection and try again.",
  'modal.passwordReset.error.captcha': 'Please complete the verification check and try again.',
  'modal.passwordReset.error.rateLimited':
    'Too many requests. Please wait a few minutes and try again.',
  'modal.passwordReset.error.tooSoon':
    'Please wait {seconds} seconds before requesting another code.',
  'modal.passwordReset.error.codeFormat': 'Enter the 6-digit code from the email.',
  'modal.passwordReset.error.codeInvalid': 'That code is incorrect or has expired.',
  'modal.passwordReset.error.codeLocked':
    'Too many incorrect codes. Request a new code to continue.',
  'modal.passwordReset.error.requirementsUnmet':
    "Your password doesn't meet every requirement below.",
  'modal.passwordReset.error.mismatch': "The passwords don't match.",
  'modal.passwordReset.error.sessionExpired':
    'Your reset session expired. Request a new code to continue.',
  'modal.passwordReset.error.passwordRejected':
    "That password can't be used. Choose one that meets every requirement and that you haven't used recently.",
  // PLANNER-PALM-UAT-2026-11-03-001 U1: a validation refusal is worded by the FIELD it names (`classifyResetRefusal`
  // `invalidField`), never "Something went wrong". The code and the new password reuse their own leaves.
  'modal.passwordReset.error.invalidField.email': 'Enter the email address on your account.',
  'modal.passwordReset.error.invalidField.subdomain':
    "We couldn't tell which company this reset is for. Open the sign-in page from your company's own address and try again.",
  'modal.passwordReset.error.invalidField.other':
    'Part of the request was missing. Close this window and start again.',
  // Q-EE-22 "Plain, retry" — the ruled words `@puffin-core/mfa-ui` carries as `error_change_not_recorded`, reused
  // when completing a reset became a RECORDED change (503 `AUDIT_RECORD_UNAVAILABLE`, nothing changed).
  'modal.passwordReset.error.notRecorded':
    'Your change was not made because it could not be recorded. Please try again in a moment.',
  'modal.passwordReset.error.failed': 'Something went wrong. Please try again.',

  /* Rate limit. The CLIENT throttle's lock notice and attempts-left warning are no longer here:
     since Q-UMB-21 (2026-09-24) they are `@puffin-core/form-security`'s `<RateLimitNotice>`,
     in the `formSecurity` namespace — the one notice every throttled form renders. */
  'login.error.rateLimited':
    'Too many attempts. Please wait {seconds} seconds before trying again.',

  /* LOGIN-CONVERGENCE W7 (2026-10-11): the words for a refused sign-in. The `login.error.*` status
     classes are `translateAuthError`'s (they were English constants in `errorTranslator.js`, which
     now reads them from here); the `login.refusal.*` leaves word the refusal codes every sign-in
     server in the fleet answers (`utils/signInRefusal.js`). */
  'login.error.credentials': 'Invalid email or password',
  'login.error.mfa': 'Invalid verification code',
  'login.error.server': 'Server error. Please try again.',
  // The coordinator's ruling (2026-10-19): a sign-in whose stores cannot be read is refused 503 LOCATIONS_UNAVAILABLE.
  'login.error.locationsUnavailable': "We couldn't load your stores. Try again in a moment.",
  'login.error.network': 'Network error. Please check your connection.',
  'login.error.rateLimit': 'Too many attempts. Please wait and try again.',
  'login.error.badRequest': 'There was a problem with your request. Please try again.',
  'login.error.mfaEnrollmentRequired':
    'Two-step verification must be set up again before you can sign in.',
  'login.error.passkeySetupRequired':
    'Your company requires a passkey for your role. Set one up to finish signing in.',
  'login.error.pinSetupRequired':
    'Your company requires a PIN for your role. Set a PIN on this register to finish signing in.',
  /* D1 family 2: the person proved who they are, and their role may not sign in on this device (`device:login`). */
  'login.error.deviceLoginNotPermitted':
    'Your account does not have permission to sign in on this device. Contact your store manager.',
  'login.error.mfaCodeInvalid': "That code didn't match. Check your authenticator and try again.",
  'login.error.mfaChallengeInvalid':
    'Your verification session is no longer valid. Please sign in again.',
  'login.error.mfaLocked': 'Too many attempts. Please sign in again.',
  'login.error.mfaSessionExpired': 'Your verification session expired. Please sign in again.',
  /* ECOSYSTEM-EMAIL-001 Q-EE-35: a one-time code for a method the person did not enrol. Terminal: a fresh sign-in
     lists the methods actually set up. */
  'login.error.mfaMethodNotEnrolled':
    "That verification method isn't set up for your account. Please sign in again.",
  /* D1 family 2: a method the verify route does not serve (backup codes off, a one-time code on a platform verify, no
     passkey wired). Terminal: a fresh sign-in lists the methods the route serves. */
  'login.error.mfaMethodUnsupported':
    "That verification method isn't available here. Please sign in again.",
  // PERMISSIONS-CANON-001 S13 A1: a verify whose challenge store cannot answer is refused 503
  // MFA_CHALLENGE_STORE_UNAVAILABLE. Nothing was checked, so it is never "wrong code"; retrying is safe.
  'login.error.mfaUnavailable': 'Verification is unavailable right now. Try again in a moment.',
  /* W4: the blank-credential gate (FVA-001) — the form's own check, before anything is sent. */
  'login.error.credentialsRequired': 'Enter your email and password to sign in.',
  /* W10: a submit the CAPTCHA policy holds (a keyboard submit past the disabled button). */
  'login.error.captchaRequired': 'Complete the security check to sign in.',
  /* W14: a credential that breaks its descriptor's shape, when the surface gave no sentence of its own. */
  'login.error.credentialsInvalid': 'Check what you entered and try again.',
  'login.refusal.captchaFailed': 'The security check did not pass. Complete it again to sign in.',
  'login.refusal.accountNotActive':
    'This account is not active. Ask whoever manages your access to restore it.',
  'login.refusal.companyInactive':
    "This company's account is not active. Ask whoever manages it to restore it.",
  'login.refusal.accountUnavailable':
    'This account cannot sign in here. Ask whoever manages your access.',
  'login.refusal.signInUnavailable':
    'Sign-in is unavailable right now. Try again in a few minutes.',
  /* LOGIN-CONVERGENCE: MFA is on, but no method the person set up can be served here (`MFA_NO_USABLE_METHOD`). The
     same sentence as mfa-ui's `error_no_usable_method` (`noUsableMethodWords.contract`). */
  'login.refusal.mfaNoUsableMethod':
    'None of the second factors you have set up can be used to sign in here. Sign in where you set it up, or ask your administrator to reset your second factor.',
  /* W6: shown on the sign-in form once a password reset has finished. */
  'login.passwordReset.done': 'Your password is set. Sign in with it now.',
  /* W8: the default label of a sign-in's own cancel action. */
  'login.cancel': 'Cancel',

  /* Contact manager modal */
  'modal.contact.title': 'Request Access',
  'modal.contact.formLabel': 'Access request form',
  'modal.contact.body': 'Submit a request to {companyName} for account access.',
  'modal.contact.fallbackCompany': 'your company',
  'modal.contact.name': 'Full Name',
  'modal.contact.email': 'Email',
  'modal.contact.message': 'Message (optional)',
  'modal.contact.submit': 'Send Request',
  'modal.contact.submit.loading': 'Sending...',
  'modal.contact.success':
    'Your request has been sent to your store manager. You will receive an email confirmation shortly.',
  'modal.contact.close': 'Close',
  'modal.contact.error.invalid': 'Please check your name and email address, then try again.',
  'modal.contact.error.rateLimited': 'Too many requests. Please wait a few minutes and try again.',
  'modal.contact.error.failed': "We couldn't send your request. Please try again.",

  /* Modal generic */
  'modal.closeLabel': 'Close',

  /* Subdomain prompt (cross-domain mode) */
  'subdomain.label': 'Your company subdomain',
  'subdomain.validating': 'Checking subdomain...',
  'subdomain.foundCompany': 'Signing in to {companyName}',
  'subdomain.welcomeBack': 'Welcome back to {subdomain}',
  'subdomain.useDifferentCompany': 'Use a different company',
  'subdomain.cantFind': "Don't know your subdomain?",
  'subdomain.lookupAction': 'Find my company',

  /* Subdomain validation failures (AAS-I18N-002 W2 / ruling 4).
     `useSubdomainValidation` set these as plain call arguments and as an object
     PROPERTY (`errored.error = '…'`) — RECIPE miss-classes 7 and 2, inside a
     package (miss-class 9), so every app's census honestly read 0. Only the
     first two were in the 30-site HIGH list; the third is a measured recall gap. */
  'subdomain.error.required': 'Subdomain is required',
  'subdomain.error.invalidFormat': 'Invalid subdomain format',
  'subdomain.error.validationFailed':
    'An error occurred while validating the subdomain. Please try again.',

  /* Subdomain lookup modal */
  'modal.subdomainLookup.title': 'Find Your Company',
  'modal.subdomainLookup.description':
    'Enter your email address and we will send you information about your company subdomain.',
  'modal.subdomainLookup.emailLabel': 'Email Address',
  'modal.subdomainLookup.submit': 'Email Me My Company Info',
  'modal.subdomainLookup.submitting': 'Sending...',
  'modal.subdomainLookup.retry': 'Try Again',
  'modal.subdomainLookup.successTitle': 'Email Sent',
  'modal.subdomainLookup.success':
    'If an account exists with this email, you will receive an email with your company subdomain information.',
  'modal.subdomainLookup.formLabel': 'Company lookup form',
  // N138: the lookup's validation and refusal words (were English literals in the hook).
  'modal.subdomainLookup.error.emailRequired': 'Email is required',
  'modal.subdomainLookup.error.emailInvalid': 'Enter a valid email address',
  'modal.subdomainLookup.error.rateLimited':
    'Too many requests. Please wait a few minutes and try again.',
  'modal.subdomainLookup.error.failed': 'We could not send the email. Please try again.',
});

/**
 * Interpolate this corpus's `{param}` placeholders.
 *
 * I18N-RENDER-002 Phase 0 (2026-08-20): bound from the ONE shared implementation, which also fixes
 * two latent defects the hand-rolled version carried. It was PARAM-driven — it iterated the params
 * and built `new RegExp('\\{' + key + '\\}')` per key — so:
 *
 *   - a param VALUE containing `$&`, `` $` `` or `$1` was expanded by `String.replace` as a
 *     **replacement pattern** rather than inserted literally. `{companyName}` and `{email}` are
 *     user-controlled and reach the login screen, the first screen every user sees;
 *   - a param KEY containing a regex metacharacter would have built a malformed pattern.
 *
 * The shared implementation is TEMPLATE-driven with a callback replacer, so neither is reachable.
 *
 * ⚠️ `braces: 'single'` matches this corpus (`{companyName}`, `{seconds}`, `{attempts}`), and
 * `onMissing: 'keep'` preserves the pinned behaviour that an unsupplied placeholder is echoed back
 * (`tests/unit/strings.test.js:41`).
 *
 * @param {string} template - Template string with `{key}` placeholders
 * @param {Object} [params] - Key-value pairs to interpolate
 * @returns {string} Interpolated string
 */
export const interpolate = createInterpolator({ braces: 'single', onMissing: 'keep' });
